/**
 * Error Types and Definitions
 * 
 * Standardized error types for API error handling
 */

export enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  RATE_LIMIT = 'RATE_LIMIT',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}

export interface ApiError {
  type: ErrorType;
  message: string;
  status?: number;
  code?: string;
  details?: any;
  originalError?: Error;
  retryable: boolean;
}

export class NetworkError implements ApiError {
  type = ErrorType.NETWORK;
  retryable = true;

  constructor(
    public message: string,
    public originalError?: Error,
    public details?: any
  ) {}
}

export class ValidationError implements ApiError {
  type = ErrorType.VALIDATION;
  retryable = false;

  constructor(
    public message: string,
    public status: number = 400,
    public details?: any,
    public originalError?: Error
  ) {}
}

export class AuthenticationError implements ApiError {
  type = ErrorType.AUTHENTICATION;
  retryable = false;

  constructor(
    public message: string = 'Authentication failed',
    public status: number = 401,
    public details?: any,
    public originalError?: Error
  ) {}
}

export class AuthorizationError implements ApiError {
  type = ErrorType.AUTHORIZATION;
  retryable = false;

  constructor(
    public message: string = 'You do not have permission to perform this action',
    public status: number = 403,
    public details?: any,
    public originalError?: Error
  ) {}
}

export class NotFoundError implements ApiError {
  type = ErrorType.NOT_FOUND;
  retryable = false;

  constructor(
    public message: string = 'Resource not found',
    public status: number = 404,
    public details?: any,
    public originalError?: Error
  ) {}
}

export class ServerError implements ApiError {
  type = ErrorType.SERVER;
  retryable = true;

  constructor(
    public message: string = 'Server error occurred',
    public status: number = 500,
    public details?: any,
    public originalError?: Error
  ) {}
}

export class RateLimitError implements ApiError {
  type = ErrorType.RATE_LIMIT;
  retryable = true;

  constructor(
    public message: string = 'Too many requests. Please try again later',
    public status: number = 429,
    public details?: any,
    public originalError?: Error
  ) {}
}

export class TimeoutError implements ApiError {
  type = ErrorType.TIMEOUT;
  retryable = true;

  constructor(
    public message: string = 'Request timed out. Please try again',
    public originalError?: Error,
    public details?: any
  ) {}
}

export class UnknownError implements ApiError {
  type = ErrorType.UNKNOWN;
  retryable = false;

  constructor(
    public message: string = 'An unexpected error occurred',
    public status?: number,
    public details?: any,
    public originalError?: Error
  ) {}
}

